import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Check } from "lucide-react";
import { cn } from "../lib/utils";
import GlobalLoader from "./ui/GlobalLoader";
import { getSunbeds, Sunbed, SunbedPricing } from "../services/sunbedsApi";
import { getSunbedMembershipPlans, SunbedMembershipPlanApi } from "../services/sunbeds";
import { useAuth } from "../auth/AuthContext";

import { Service } from "../types/calendar";

interface Props {
  setSelectedServices: ((services: Service[]) => void) | React.Dispatch<React.SetStateAction<Service[]>>;
  setStep: (
    step: "selection" | "customer" | "summary" | "payment" | "finalPayment"
  ) => void;
  isAddingService?: boolean;
  setIsAddingService?: (value: boolean) => void;
  disabledServices?: Service[];
}

const SunbedSelection: React.FC<Props> = ({
  setSelectedServices,
  setStep,
  isAddingService,
  setIsAddingService,
  disabledServices,
}) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || "£";
  const [sunbeds, setSunbeds] = useState<Sunbed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSunbed, setSelectedSunbed] = useState<Sunbed | null>(null);
  const [selectedPricing, setSelectedPricing] = useState<SunbedPricing | null>(null);
  const [selectedMinutes, setSelectedMinutes] = useState<number>(0);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [catalogTab, setCatalogTab] = useState<"packages" | "memberships">("packages");
  const [membershipPlans, setMembershipPlans] = useState<SunbedMembershipPlanApi[]>([]);
  const [membershipsError, setMembershipsError] = useState<string | null>(null);
  const [membershipsLoading, setMembershipsLoading] = useState(false);

  const loadMembershipPlans = async () => {
    setMembershipsLoading(true);
    setMembershipsError(null);
    try {
      const plansRes = await getSunbedMembershipPlans();
      const raw = (plansRes as any)?.data ?? plansRes;
      const list: SunbedMembershipPlanApi[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : [];
      setMembershipPlans(
        list.filter((plan) => String(plan.status || "").toLowerCase() === "active")
      );
    } catch (err: any) {
      console.error("Error fetching membership plans:", err);
      setMembershipPlans([]);
      setMembershipsError(
        err?.message || err?.response?.data?.message || "Failed to load membership plans"
      );
    } finally {
      setMembershipsLoading(false);
    }
  };

  useEffect(() => {
    const fetchSunbeds = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchedSunbeds = await getSunbeds();
        setSunbeds(fetchedSunbeds);
      } catch (err: any) {
        console.error("Error fetching sunbeds:", err);
        setError(err.message || "Failed to load sunbeds");
      } finally {
        setLoading(false);
      }
    };

    void fetchSunbeds();
    void loadMembershipPlans();
  }, []);

  useEffect(() => {
    if (catalogTab === "memberships" && membershipPlans.length === 0 && !membershipsLoading) {
      void loadMembershipPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogTab]);

  const handleSunbedClick = (sunbed: Sunbed) => {
    if (sunbed.pricing && sunbed.pricing.length > 0) {
      setSelectedSunbed(sunbed);
      // If only one pricing option, auto-select it
      if (sunbed.pricing.length === 1) {
        const pricing = sunbed.pricing[0];
        setSelectedPricing(pricing);
        if (pricing.type === "per_minute") {
          // Extract default minutes from value if available
          const match = pricing.value.match(/\d+/);
          setSelectedMinutes(match ? parseInt(match[0]) : 0);
        }
        setShowPricingModal(true);
      } else {
        setShowPricingModal(true);
      }
    } else {
      setError("This sunbed has no pricing options available");
    }
  };

  const handlePricingSelect = (pricing: SunbedPricing) => {
    setSelectedPricing(pricing);
    if (pricing.type === "per_minute") {
      // Extract default minutes from value if available
      const match = pricing.value.match(/\d+/);
      setSelectedMinutes(match ? parseInt(match[0]) : 0);
    } else {
      setSelectedMinutes(0);
    }
  };

  const handleConfirmSelection = () => {
    if (!selectedSunbed || !selectedPricing) return;

    // Calculate price and duration based on pricing type
    let price = 0;
    let duration = "";
    let finalMinutes = 0;

    if (selectedPricing.type === "fixed") {
      // Extract minutes from value (e.g., "3 mins" -> 3)
      const match = selectedPricing.value.match(/\d+/);
      finalMinutes = match ? parseInt(match[0]) : 0;
      price = typeof selectedPricing.price === "string"
        ? parseFloat(selectedPricing.price)
        : selectedPricing.price;
      duration = `${finalMinutes} mins`;
    } else {
      // per_minute pricing
      finalMinutes = selectedMinutes || 0;
      const pricePerMinute = typeof selectedPricing.price === "string"
        ? parseFloat(selectedPricing.price)
        : selectedPricing.price;
      price = finalMinutes * pricePerMinute;
      duration = `${finalMinutes} mins`;
    }

    // Create service object compatible with existing Service interface
    const service: Service = {
      id: `sunbed-${selectedSunbed.id}`,
      name: selectedSunbed.name,
      duration,
      price: price.toFixed(2),
      sunbedId: selectedSunbed.id,
      selectedPricingId: selectedPricing.id,
      selectedMinutes: finalMinutes,
      pricing: selectedSunbed.pricing,
      company_id: selectedSunbed.company_id || user?.company?.id || (user as any)?.company_id,
    };

    addToCart(service);

    // Reset modal state
    setShowPricingModal(false);
    setSelectedSunbed(null);
    setSelectedPricing(null);
    setSelectedMinutes(0);
  };

  const addToCart = (service: Service) => {
    const isDisabled = disabledServices?.some((s) => s.id === service.id);
    if (isDisabled) return;

    if (isAddingService && setIsAddingService) {
      if (typeof setSelectedServices === "function") {
        try {
          (setSelectedServices as React.Dispatch<React.SetStateAction<Service[]>>)((prev: Service[]) => [...prev, service]);
        } catch {
          setSelectedServices([service]);
        }
      }
      setIsAddingService(false);
      setStep("summary");
    } else {
      setSelectedServices([service]);
      setStep("customer");
    }
  };

  const handleMembershipClick = (plan: SunbedMembershipPlanApi) => {
    addToCart({
      id: `membership-${plan.id}`,
      name: plan.name,
      duration: plan.interval_count === 1 ? plan.interval : `${plan.interval_count} ${plan.interval}s`,
      price: Number(plan.price).toFixed(2),
      membershipPlanId: plan.id,
      company_id: user?.company?.id || (user as any)?.company_id,
      description: plan.entitlement === "unlimited"
        ? "Unlimited membership"
        : `${plan.minutes_per_period ?? 0} minutes per period`,
    });
  };

  const handleCancelSelection = () => {
    setShowPricingModal(false);
    setSelectedSunbed(null);
    setSelectedPricing(null);
    setSelectedMinutes(0);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-[400px]">
        <GlobalLoader initialPageLoad={false} compensateSidebar={false} description="Loading sunbeds..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center text-red-700">
        <p className="font-bold">Error loading sunbeds</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

    return (
    <div className="relative h-full flex flex-col">
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={() => setCatalogTab("packages")}
          className={cn(
            "rounded-full px-4 py-1.5 text-sm border",
            catalogTab === "packages"
              ? "bg-[#885ABB] text-white border-[#885ABB]"
              : "bg-white text-gray-700 border-gray-200"
          )}
        >
          Packages
        </button>
        <button
          type="button"
          onClick={() => setCatalogTab("memberships")}
          className={cn(
            "rounded-full px-4 py-1.5 text-sm border",
            catalogTab === "memberships"
              ? "bg-[#885ABB] text-white border-[#885ABB]"
              : "bg-white text-gray-700 border-gray-200"
          )}
        >
          Memberships
        </button>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1">
        <motion.div
          key={catalogTab}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-col gap-3"
        >
          {catalogTab === "packages" && sunbeds.length === 0 && (
            <div className="text-center py-12 text-sm text-gray-400">
              No sunbeds available at the moment.
            </div>
          )}
          {catalogTab === "packages" && sunbeds.map((sunbed) => {
            const isDisabled = disabledServices?.some((s) => s.id === `sunbed-${sunbed.id}`);

            // Get default pricing for display
            const defaultPricing = sunbed.pricing?.[0];
            let displayPrice = "—";
            let displayDuration = "—";

            if (defaultPricing) {
              if (defaultPricing.type === "fixed") {
                const match = defaultPricing.value.match(/\d+/);
                displayDuration = match ? `${match[0]} mins` : defaultPricing.value;
                displayPrice = `${typeof defaultPricing.price === "string"
                  ? parseFloat(defaultPricing.price).toFixed(2)
                  : defaultPricing.price.toFixed(2)}`;
              } else {
                displayDuration = "Per minute";
                displayPrice = `${typeof defaultPricing.price === "string"
                  ? parseFloat(defaultPricing.price).toFixed(2)
                  : defaultPricing.price.toFixed(2)}/min`;
              }
            }

            return (
              <motion.div
                key={sunbed.id}
                variants={itemVariants}
                className={cn(
                  "group relative flex items-center justify-between p-5 bg-white border border-gray-100 rounded-xl transition-all duration-300 shadow-sm",
                  isDisabled ? "opacity-40 cursor-not-allowed grayscale" : "cursor-pointer"
                )}
                onClick={() => {
                  if (!isDisabled) {
                    handleSunbedClick(sunbed);
                  }
                }}
              >
                <div className="flex-1">
                  <h3 className="text-base font-[600] text-[#885ABB]">
                    {sunbed.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 text-gray-400">
                    <Clock size={12} />
                    <span className="text-xs font-medium">{displayDuration}</span>
                  </div>
                </div>

                <div className="text-right flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    {isDisabled && (
                      <div className="flex items-center gap-1 opacity-70">
                        <Check size={10} className="text-[#885ABB]" strokeWidth={4} />
                        <span className="text-[9px] font-[600] text-[#885ABB] uppercase">Selected</span>
                      </div>
                    )}
                    <span className="text-lg font-[600] text-[#885ABB]">
                      {currencySymbol}{displayPrice}
                    </span>
                  </div>
                </div>

                {/* Left Indicator bar */}
                <div className={cn(
                  "absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full transition-colors duration-300",
                  isDisabled ? "bg-gray-300" : "bg-[#885ABB]"
                )} />
              </motion.div>
            );
          })}
          {catalogTab === "memberships" && (
            membershipsLoading ? (
              <div className="flex justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent" />
              </div>
            ) : membershipsError ? (
              <div className="text-center py-12 space-y-3">
                <p className="text-sm text-red-600">{membershipsError}</p>
                <button
                  type="button"
                  onClick={() => void loadMembershipPlans()}
                  className="text-sm font-semibold text-[#885ABB] hover:underline"
                >
                  Retry
                </button>
              </div>
            ) : membershipPlans.length === 0 ? (
              <div className="text-center py-12 text-sm text-gray-400">
                No membership plans yet. Create them under Sunbeds → Memberships.
              </div>
            ) : (
              membershipPlans.map((plan) => {
                const isDisabled = disabledServices?.some((s) => s.id === `membership-${plan.id}`);
                return (
                  <div
                    key={plan.id}
                    className={cn(
                      "group relative flex items-center justify-between p-5 bg-white border border-gray-100 rounded-xl shadow-sm",
                      isDisabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:border-[#885ABB]/40"
                    )}
                    onClick={() => {
                      if (!isDisabled) handleMembershipClick(plan);
                    }}
                  >
                    <div>
                      <p className="font-semibold text-[#402B69]">{plan.name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {plan.entitlement === "unlimited"
                          ? "Unlimited"
                          : `${plan.minutes_per_period ?? 0} min / period`}
                        {plan.rollover ? " · rollover" : ""}
                      </p>
                    </div>
                    <p className="font-semibold text-[#885ABB]">
                      {currencySymbol}
                      {Number(plan.price).toFixed(2)}
                    </p>
                  </div>
                );
              })
            )
          )}
        </motion.div>
      </div>

      {/* Pricing Selection Overlay - Now Localized to Sidebar Area */}
      <AnimatePresence>
        {showPricingModal && selectedSunbed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[100] flex flex-col bg-[#F9F9F9]/95 backdrop-blur-[2px]"
          >
            <motion.div
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 20, opacity: 0 }}
              className="flex-1 flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Internal Header */}
              <div className="p-6 pb-4">
                <h3 className="text-xl font-[600] text-[#402B69]">Pricing Method</h3>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mt-1">
                  {selectedSunbed.name}
                </p>
              </div>

              {/* Scrollable List */}
              <div className="flex-1 overflow-y-auto p-6 pt-0 space-y-4 custom-scrollbar">
                {selectedSunbed.pricing?.map((pricing) => {
                  const isSelected = selectedPricing?.id === pricing.id;
                  return (
                    <div key={pricing.id} className="space-y-3">
                      <div
                        className={cn(
                          "relative flex items-center justify-between p-4 rounded-2xl border transition-all duration-300 cursor-pointer",
                          isSelected
                            ? "bg-white border-[#885ABB] shadow-md ring-1 ring-[#885ABB]/10"
                            : "bg-white border-gray-100 hover:border-gray-200"
                        )}
                        onClick={() => handlePricingSelect(pricing)}
                      >
                        <div className="flex-1">
                          <p className={cn(
                            "text-[14px] font-[600]",
                            isSelected ? "text-[#885ABB]" : "text-[#402B69]"
                          )}>
                            {pricing.type === "fixed" ? "Fixed Session" : "Flexi Minutes"}
                          </p>
                          <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                            {pricing.label || (pricing.type === "fixed" ? pricing.value : "Flexible")}
                          </p>
                        </div>

                        <div className="text-right">
                          <span className={cn(
                            "text-base font-[600]",
                            isSelected ? "text-[#885ABB]" : "text-[#402B69]"
                          )}>
                            {currencySymbol}{typeof pricing.price === "string" ? parseFloat(pricing.price).toFixed(2) : pricing.price.toFixed(2)}
                          </span>
                          {pricing.type === "per_minute" && <p className="text-[9px] text-gray-400 font-bold uppercase">/ min</p>}
                        </div>

                        <div className={cn(
                          "absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full",
                          isSelected ? "bg-[#885ABB]" : "bg-transparent"
                        )} />
                      </div>

                      {isSelected && pricing.type === "per_minute" && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                          className="bg-white p-4 rounded-2xl border border-[#885ABB]/20 space-y-3 shadow-sm"
                        >
                          <div>
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-1">Minutes Required</label>
                            <input
                              type="number" value={selectedMinutes || ""}
                              onChange={(e) => setSelectedMinutes(parseInt(e.target.value) || 0)}
                              className="w-full h-10 px-4 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold text-[#402B69] focus:bg-white focus:ring-2 focus:ring-[#885ABB]/10 outline-none transition-all"
                              placeholder="e.g. 15"
                            />
                          </div>
                          {selectedMinutes > 0 && (
                            <div className="pt-2 border-t border-gray-50 flex justify-between items-center text-[#10b981]">
                              <span className="text-[10px] font-bold uppercase tracking-wider">Total Due</span>
                              <span className="text-sm font-extrabold">{currencySymbol}{(selectedMinutes * (typeof pricing.price === "string" ? parseFloat(pricing.price) : pricing.price)).toFixed(2)}</span>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Action Footer */}
              <div className="p-6 bg-white border-t border-gray-50 flex gap-3">
                <button
                  onClick={handleCancelSelection}
                  className="flex-1 h-11 bg-white border border-gray-200 text-[#402B69] rounded-xl font-bold hover:bg-gray-50 text-xs transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSelection}
                  disabled={!selectedPricing || (selectedPricing.type === "per_minute" && selectedMinutes <= 0)}
                  className="flex-[2] h-11 bg-[#885ABB] text-white rounded-xl font-bold hover:bg-[#7748A4] shadow-lg shadow-[#885ABB]/10 text-xs disabled:opacity-50 transition-all uppercase tracking-widest"
                >
                  Confirm Method
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SunbedSelection;
